// BalancesLayout.jsx - Streamlined + Safe + Independent Duplicates + Add Section Modal
import React, { useState, useEffect, useCallback } from 'react';
import axios from "axios";
import config from '../config/config';
import { useParams, Link } from 'react-router-dom';
// ── Components ──────────────────────────────────────────────────────────────
import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import HeroSection from "../components/HeroSection";
import Banner from "../components/Banner";
import CategorySection from "../components/Categories";
import ProductSection from "../components/ProductSection";
import CartSection from "../components/CartSection";
import FooterSection from "../components/FooterSection";
import ProductView from "../components/ProductView";
import SearchWidget from "../components/SearchWidget";

// ── Admin Components ────────────────────────────────────────────────────────
import FlashBarAdmin from "../pages/FlashBar";
import NavigationAdmin from "../pages/NavigationBar";
import HeroAdmin from "../pages/HeroAdmin";
import BannerAdmin from "../pages/BannerAdmin";
import CategoryAdmin from "../pages/CategoryAdmin";
import ProductAdmin from "../pages/ProductAdmin";
import CartAdmin from "../pages/CartAdmin";
import FooterAdmin from "../pages/FooterAdmin";
import SearchAdmin from "../pages/SearchAdmin";
import ProductViewAdmin from "../pages/ProductView";

// ── Lucide Icons (npm i lucide-react) ──────────────────────────────────────
import { LayoutDashboard, Zap, Grid, ShoppingBag, Footprints, Image as ImageIcon, Star, Plus, X } from 'lucide-react';

function BalancesLayout() {

  const { page_index } = useParams();
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  const [sectionOrder, setSectionOrder] = useState([]);
  const [globalConfigs, setGlobalConfigs] = useState({});
  const [customConfigs, setCustomConfigs] = useState({});

  const [editingSlug, setEditingSlug] = useState(null);

  // Add Section Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);

  const CACHE_KEY = 'cms_layout_cache_ken8';
  const CACHE_TTL = 60 * 1000;

  // Available sections for adding
  const availableSections = [
    { id: 'navbar', name: 'Navigation Bar', icon: LayoutDashboard, color: 'bg-indigo-100 text-indigo-600' },
    { id: 'flashbar', name: 'Flash Bar', icon: Zap, color: 'bg-amber-100 text-amber-600' },
    { id: 'categories', name: 'Categories', icon: Grid, color: 'bg-green-100 text-green-600' },
    { id: 'products', name: 'Products', icon: ShoppingBag, color: 'bg-blue-100 text-blue-600' },
    { id: 'footer', name: 'Footer', icon: Footprints, color: 'bg-gray-100 text-gray-600' },
    { id: 'banner', name: 'Banner', icon: ImageIcon, color: 'bg-purple-100 text-purple-600' },
    { id: 'hero', name: 'Hero', icon: Star, color: 'bg-pink-100 text-pink-600' },
  ];

  // ── Load Layout ────────────────────────────────────────────────────────────
  useEffect(() => {
    const loadLayout = async () => {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        try {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL) {
            setSectionOrder(data.sectionOrder || []);
            setGlobalConfigs(data.global || {});
            setCustomConfigs(data.custom || {});
            setLoading(false);
            return;
          }
        } catch (e) {
          localStorage.removeItem(CACHE_KEY);
        }
      }

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

        const [
          flashRes, navRes, heroRes, bannerRes,
          catRes, prodRes, cartRes, footerRes, layoutRes, searchRes
        ] = await Promise.all([
          axios.get(config.API_URL + '/api/pullFlashBarSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullNavBarSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullHeroSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullBannerSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullCategorySettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullProductSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullCartSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullFooterSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullLayoutSettings/'+page_index, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(config.API_URL + '/api/pullSearchSettings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }).catch(() => ({ data: {} })),
        ]);

        const newGlobal = {
          flash: flashRes.data.flashBarSettings || null,
          nav: navRes.data.navBarSettings || null,
          hero: heroRes.data.heroSettings || null,
          banner: bannerRes.data.bannerSettings || null,
          categories: catRes.data.categorySettings || null,
          products: prodRes.data.productSettings || null,
          cart: cartRes.data.cartSettings || layoutRes.data.cartSettings || null,
          footer: footerRes.data.footerSettings || null,
          search: searchRes.data.searchSettings || null,
          'product-view': prodRes.data.productViewSettings || null,
        };

        setGlobalConfigs(newGlobal);
        setSectionOrder(layoutRes.data?.order || []);

        localStorage.setItem(CACHE_KEY, JSON.stringify({
          data: { sectionOrder: layoutRes.data?.order || [], global: newGlobal, custom: {} },
          timestamp: Date.now(),
        }));
      } catch (err) {
        console.error("Load error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadLayout();
  }, [token]);

  // ── Load custom config on demand ──────────────────────────────────────────
  const loadCustomConfig = useCallback(async (slug) => {
    if (customConfigs[slug]) return customConfigs[slug];

    try {
      const res = await axios.get(
        `${config.API_URL}/api/pullCustomSettings/${slug}`,
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
      );
      const customData = res.data.customSettings || {};
      setCustomConfigs(prev => ({ ...prev, [slug]: customData }));
      return customData;
    } catch (err) {
      console.error("Failed to load custom:", err);
      return {};
    }
  }, [token, customConfigs]);

  // ── Render Section ─────────────────────────────────────────────────────────
  const renderSection = (slug) => {
    const type = slug.split('-')[0];

    if (!slug.includes('-')) {
      switch (type) {
        case 'flashbar':    return globalConfigs.flash ? <FlashBar config={globalConfigs.flash} /> : null;
        case 'navbar':      return globalConfigs.nav ? <NavigationBar config={globalConfigs.nav} cartConfig={globalConfigs.cart} searchConfig={globalConfigs.search} /> : null;
        case 'search':      return globalConfigs.search ? <SearchWidget config={globalConfigs.search} isOpen={true} onClose={() => {}} /> : null;
        case 'hero':        return globalConfigs.hero ? <HeroSection config={globalConfigs.hero} /> : null;
        case 'banner':      return globalConfigs.banner ? <Banner config={globalConfigs.banner} /> : null;
        case 'categories':  return globalConfigs.categories ? <CategorySection config={globalConfigs.categories} /> : null;
        case 'products':    return globalConfigs.products ? <ProductSection config={globalConfigs.products} /> : null;
        case 'cart':        return globalConfigs.cart ? <CartSection config={globalConfigs.cart} isOpen={true} onClose={() => {}} /> : null;
        case 'footer':      return globalConfigs.footer ? <FooterSection config={globalConfigs.footer} /> : null;
        case 'product-view': return globalConfigs['product-view'] ? <ProductView config={globalConfigs['product-view']} /> : null;
        default: return null;
      }
    }

    const custom = customConfigs[slug];
    if (!custom) {
      loadCustomConfig(slug);
      return <div className="min-h-[200px] bg-gray-100 flex items-center justify-center">Loading {slug}...</div>;
    }

    switch (type) {
      case 'flashbar':    return <FlashBar config={custom} />;
      case 'navbar':      return <NavigationBar config={custom} cartConfig={customConfigs.cart} searchConfig={customConfigs.search} />;
      case 'search':      return <SearchWidget config={custom} isOpen={true} onClose={() => {}} />;
      case 'hero':        return <HeroSection config={custom} />;
      case 'banner':      return <Banner config={custom} />;
      case 'categories':  return <CategorySection config={custom} />;
      case 'products':    return <ProductSection config={custom} />;
      case 'cart':        return <CartSection config={custom} isOpen={true} onClose={() => {}} />;
      case 'footer':      return <FooterSection config={custom} />;
      case 'product-view': return <ProductView config={custom} />;
      default: return null;
    }
  };

  // ── Dynamic Editing Modal ──────────────────────────────────────────────────
  if (editingSlug) {
    const slug = editingSlug;
    const baseType = slug.split('-')[0];
    const isDuplicate = slug.includes('-');

    const adminComponentMap = {
      flashbar: FlashBarAdmin,
      navbar: NavigationAdmin,
      search: SearchAdmin,
      hero: HeroAdmin,
      banner: BannerAdmin,
      categories: CategoryAdmin,
      products: ProductAdmin,
      cart: CartAdmin,
      footer: FooterAdmin,
      'product-view': ProductViewAdmin,
    };

    const AdminComponent = adminComponentMap[baseType];
    if (!AdminComponent) {
      return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="bg-white p-12 rounded-xl shadow-2xl text-red-600">
            No editor found for {baseType}
          </div>
        </div>
      );
    }

    const currentConfig = isDuplicate
      ? (customConfigs[slug] || null)
      : (globalConfigs[baseType === 'product-view' ? 'product-view' : baseType] || null);

    if (isDuplicate && currentConfig === null) {
      loadCustomConfig(slug);
    }

    if (isDuplicate && currentConfig === null) {
      return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
          <div className="bg-white p-12 rounded-xl shadow-2xl text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-lg font-medium">Loading configuration for {slug}...</p>
          </div>
        </div>
      );
    }

    const finalConfig = currentConfig || {};

    const handleSave = async (updated) => {
      if (isDuplicate) {
        setCustomConfigs(prev => ({ ...prev, [slug]: updated }));
        try {
          await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
          const capitalized = baseType.charAt(0).toUpperCase() + baseType.slice(1);
          await axios.post(
            `${config.API_URL}/api/saveCustomSettings/${capitalized}/${slug}`,
            { settings: updated },
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } catch (err) {
          console.error("Custom save failed:", err);
          alert("Failed to save changes");
          return;
        }
      } else {
        setGlobalConfigs(prev => ({
          ...prev,
          [baseType === 'product-view' ? 'product-view' : baseType]: updated
        }));
      }
      setEditingSlug(null);
    };

    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-auto pt-10">
        <div className="bg-white w-full max-w-full rounded-xl shadow-2xl overflow-hidden">
          <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
            <h2 className="text-2xl font-bold capitalize">
              Edit {baseType.replace('-', ' ')} ({slug})
            </h2>
            <button onClick={() => setEditingSlug(null)} className="text-3xl text-gray-600 hover:text-gray-900">×</button>
          </div>
          <div className="p-6">
            <AdminComponent 
              config={finalConfig}
              slug={slug}
              onSave={handleSave}
              onClose={() => setEditingSlug(null)}
            />
          </div>
        </div>
      </div>
    );
  }

  // ── Add New Section Handler ────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!selectedSection) return;

    setAdding(true);
    setAddError(null);
    setAddSuccess(null);
    //alert(selectedSection);

    try {
        const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");
      const response = await axios.post(
        `${config.API_URL}/api/addNewSection`,
        { section: selectedSection },
        {
          headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          "Authorization": `Bearer ${token}`,
        },
        withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        setAddSuccess(`Added ${selectedSection} (${response.data.new_slug || 'new section'})`);
        setTimeout(() => {
          setIsAddModalOpen(false);
          setSelectedSection(null);
          setAddSuccess(null);
          window.location.reload(); // Show new section
        }, 1800);
      } else {
        setAddError(response.data.message || 'Failed to add section');
      }
    } catch (error) {
      console.error("Add section error:", error);
      setAddError(error.response?.data?.message || "Network error. Please try again.");
    } finally {
      setAdding(false);
    }
  };

  // ── Duplicate Section ──────────────────────────────────────────────────────
  const duplicateSection = async (originalSlug) => {
    try {
      const baseType = originalSlug.split('-')[0];

      let originalConfig;
      if (originalSlug.includes('-')) {
        originalConfig = customConfigs[originalSlug];
      } else {
        originalConfig = globalConfigs[baseType === 'product-view' ? 'product-view' : baseType];
      }

      if (!originalConfig) {
        alert("Cannot duplicate - no configuration found.");
        return;
      }

      const nextNumber = getNextDuplicateNumber(baseType);
      const newSlug = `${baseType}-${nextNumber}`;

      const newConfig = JSON.parse(JSON.stringify(originalConfig));

      if (newConfig.customStyles) {
        newConfig.customStyles.note = "This is a copy";
      }

      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      await new Promise(resolve => setTimeout(resolve, 150));

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      const capitalized = baseType.charAt(0).toUpperCase() + baseType.slice(1);
      const saveEndpoint = `${config.API_URL}/api/saveCustomSettings/${capitalized}/${newSlug}`;

      const saveRes = await axios.post(saveEndpoint, { settings: newConfig }, {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          "Authorization": `Bearer ${token}`,
        },
        withCredentials: true,
      });

      if (saveRes.data.status !== 1) {
        throw new Error("Failed to save new section");
      }

      const newOrder = [...sectionOrder];
      const originalIndex = newOrder.indexOf(originalSlug);
      newOrder.splice(originalIndex + 1, 0, newSlug);

      await axios.post(config.API_URL + "/api/saveLayoutSettings", { order: newOrder }, {
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          "Authorization": `Bearer ${token}`,
        },
        withCredentials: true,
      });

      localStorage.removeItem(CACHE_KEY);
      alert(`Duplicated as ${newSlug}! Refreshing...`);
      window.location.reload();
    } catch (error) {
      console.error("Duplication failed:", error);
      alert("Failed to duplicate section.");
    }
  };

  // ── Delete Section ─────────────────────────────────────────────────────────
  const deleteSection = async (slugToDelete) => {
    if (!window.confirm(`Delete "${slugToDelete}"? This cannot be undone.`)) return;

    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      await axios.get(
        `${config.API_URL}/api/deleteSection/${slugToDelete}`,
        {
          headers: {
            "Authorization": `Bearer ${token}`,
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
          },
          withCredentials: true,
        }
      );

      setSectionOrder(prev => prev.filter(s => s !== slugToDelete));
      localStorage.removeItem(CACHE_KEY);
      alert('Section deleted!');
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete section.");
    }
  };

  // ── Move Section ───────────────────────────────────────────────────────────
  const moveSection = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= sectionOrder.length) return;

    const newOrder = [...sectionOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setSectionOrder(newOrder);
  };

  // ── Save Layout Order ──────────────────────────────────────────────────────
  const saveLayout = async () => {
    try {
      setSaving(true);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

      if (!xsrfToken) throw new Error("No XSRF token");

      const res = await axios.post(
        config.API_URL + "/api/saveLayoutSettings",
        { order: sectionOrder },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken),
            "Authorization": `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (res.data.status === 1) {
        alert("Layout saved!");
        localStorage.removeItem(CACHE_KEY);
      } else {
        alert("Save failed.");
      }
    } catch (error) {
      alert("Failed to save layout.");
    } finally {
      setSaving(false);
    }
  };

  const getBaseType = (slug) => slug.split('-')[0];

  const getNextDuplicateNumber = (baseType) => {
    const existingNumbers = sectionOrder
      .filter(s => s.startsWith(baseType + '-'))
      .map(s => {
        const match = s.match(/-(\d+)$/);
        return match ? parseInt(match[1], 10) : 1;
      });
    return existingNumbers.length === 0 ? 2 : Math.max(...existingNumbers) + 1;
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading your store...</div>;
  }

  return (
    <div className="relative min-h-screen">
      {/* Top-right controls: Edit Mode + Add Section */}
      <div className="fixed top-6 left-6 z-50 flex items-center gap-4">
        <button
          onClick={() => setIsAdminMode(!isAdminMode)}
          className={`px-6 py-3 rounded-full font-bold shadow-xl transition-all ${
            isAdminMode ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'
          }`}
        >
          {isAdminMode ? 'Exit Edit Mode' : 'Enter Edit Mode'}
        </button>

        {isAdminMode && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full font-bold shadow-xl transition-all flex items-center gap-2"
          >
            <Plus size={20} />
            Add Section
          </button>
        )}
      </div>

      {/* Render all sections */}
      {sectionOrder.map((slug, index) => (
        <div key={slug} className="relative mb-0">
          {renderSection(slug)}

          {isAdminMode && (
            <div className="absolute top-2 right-2 bg-white/90 backdrop-blur p-3 rounded-lg shadow-xl flex gap-2 z-50 flex-wrap">
              <button onClick={() => setEditingSlug(slug)} className="p-2 bg-green-50 hover:bg-green-100 text-green-700 rounded text-sm">Edit</button>
              <button onClick={() => duplicateSection(slug)} className="p-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded text-sm">Duplicate</button>
              <button onClick={() => deleteSection(slug)} className="p-2 bg-red-50 hover:bg-red-100 text-red-700 rounded text-sm">Delete</button>
              <button onClick={() => moveSection(index, -1)} disabled={index === 0} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 text-sm">↑</button>
              <button onClick={() => moveSection(index, 1)} disabled={index === sectionOrder.length - 1} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 text-sm">↓</button>
            </div>
          )}
        </div>
      ))}

      {/* Save Layout Bar */}
      {isAdminMode && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-lg px-10 py-5 rounded-full shadow-2xl flex gap-8 z-50">
          <button
            onClick={saveLayout}
            disabled={saving}
            className="bg-green-600 text-white px-8 py-3 rounded-full font-bold hover:bg-green-700 disabled:opacity-60 transition"
          >
            {saving ? 'Saving...' : 'Save Layout Order'}
          </button>
          <span className="text-sm text-gray-600">↑ ↓ reorder • Edit • Duplicate • Delete sections</span>
        </div>
      )}

      {/* Add New Section Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-8 py-5 flex justify-between items-center z-10">
              <h2 className="text-2xl font-bold text-gray-900">Add New Section</h2>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-500 hover:text-gray-700">
                <X size={28} />
              </button>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 mb-10">
                {availableSections.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => setSelectedSection(type.id)}
                    className={`flex flex-col items-center p-6 border-2 rounded-xl transition-all duration-200 ${
                      selectedSection === type.id
                        ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-300'
                        : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${type.color}`}>
                      <type.icon size={32} />
                    </div>
                    <span className="font-medium text-gray-800 text-center">{type.name}</span>
                  </button>
                ))}
              </div>

              <div className="flex justify-end gap-4">
                <button
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdd}
                  disabled={!selectedSection || adding}
                  className={`px-8 py-3 rounded-lg font-medium flex items-center gap-2 ${
                    selectedSection && !adding
                      ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {adding ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-white"></div>
                      Adding...
                    </>
                  ) : (
                    'Add Section'
                  )}
                </button>
              </div>

              {addError && <p className="mt-6 text-red-600 text-center">{addError}</p>}
              {addSuccess && <p className="mt-6 text-green-600 text-center">{addSuccess}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BalancesLayout;